import { useEffect, useState } from "react";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { getStatementInvoice, type StatementInvoice } from "../../api/statementConfig.ts";
import { Tag } from "./components.tsx";
import { fetchPayoutTarget, PayoutTargetPanel, type PayoutTarget } from "./payoutTargetPanel.tsx";
import { StatementSheetsPanel } from "./statementSheetsPanel.tsx";

interface Props {
  invoiceId: number;
  onClose: () => void;
}

interface FeeEntry {
  currency?: string;
  revenueShareAmount?: number;
  totalFee?: number;
}

interface SyncInfo {
  Method?: string;
  Status?: string;
  MoveIds?: number[];
  At?: string;
  Error?: string;
}

/**
 * Read-only invoice-info popup for the statementAggregate grid's Invoice Number
 * hyperlink (LinkID 199). Shows period, lifecycle state + approval, amounts
 * (derived per-currency from the linked aggregates), payment status, and
 * external-sync state (SyncJson: DRAFT/POSTED). No edits — close-only.
 */
export function StatementInvoiceInfoModal({ invoiceId, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState<StatementInvoice | null>(null);
  const [ptTarget, setPtTarget] = useState<PayoutTarget | null>(null);
  const [ptLoading, setPtLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setPtTarget(null);
    getStatementInvoice(invoiceId)
      .then((r) => {
        if (!live) return;
        setInv(r);
        if (r) {
          setPtLoading(true);
          fetchPayoutTarget(r.OperatorID, r.PeriodEnd)
            .then((t) => { if (live) setPtTarget(t); })
            .finally(() => { if (live) setPtLoading(false); });
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [invoiceId]);

  const fees = parseFees(inv?.BreakdownJson);
  const sync = parseSync(inv?.SyncJson);
  const approver = parseApprover(inv?.ApprovedJson);

  return (
    <GridEditModal
      title={inv ? `Invoice ${inv.InvoiceNr}` : "Invoice"}
      readOnly
      cancelLabel="Close"
      onClose={onClose}
      onSave={() => { /* read-only */ }}
    >
      {loading ? (
        <p>Loading…</p>
      ) : !inv ? (
        <p>Invoice not available — it may not exist, or you may not have access to this operator.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", minWidth: 0 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              rowGap: "var(--space-1)",
              columnGap: "var(--space-3)",
              fontSize: "var(--text-sm)",
            }}
          >
            <Label>Invoice</Label><span style={mono}>{inv.InvoiceNr}</span>
            {inv.Tier && (
              <>
                <Label>Tier</Label>
                <span>{inv.Tier === "INBOUND" ? <Tag variant="info">{inv.Tier}</Tag> : <Tag>{inv.Tier}</Tag>}</span>
              </>
            )}
            {inv.Tier === "INBOUND" ? (
              <>
                <Label>Reseller (customer)</Label>
                <span>{inv.ResellerName ?? inv.OperatorName}{inv.ResellerID != null ? <span style={muted}> · #{inv.ResellerID}</span> : null}</span>
                <Label>Operator</Label><span style={muted}>{inv.OperatorName} · #{inv.OperatorID}</span>
              </>
            ) : (
              <>
                <Label>Operator</Label><span>{inv.OperatorName} <span style={muted}>· #{inv.OperatorID}</span></span>
                {inv.ResellerName && (<><Label>Reseller</Label><span>{inv.ResellerName}{inv.ResellerID != null ? <span style={muted}> · #{inv.ResellerID}</span> : null}</span></>)}
              </>
            )}
            <Label>Period</Label>
            <span>
              <Tag>{inv.PeriodType}</Tag>{" "}
              <span style={mono}>{date(inv.PeriodStart)} → {date(inv.PeriodEnd)}</span>
            </span>
            {inv.BillingMode && (<><Label>Mode</Label><span>{inv.BillingMode}</span></>)}
            <Label>State</Label>
            <span>
              {inv.State === "Closed"
                ? <Tag variant="info">Closed</Tag>
                : <Tag variant="warning">Open — not closed</Tag>}
              {approver && <span style={muted}> · approved by #{approver.By}{approver.At ? ` (${date(approver.At)})` : ""}</span>}
            </span>
            <Label>Due</Label><span>{date(inv.DueDate)}</span>
            {inv.PaidDate && (
              <>
                <Label>Paid</Label>
                <span>{date(inv.PaidDate)}{inv.PaidAmount != null ? ` · ${money(inv.PaidAmount)}` : ""}{inv.PaymentRefNr ? ` · ${inv.PaymentRefNr}` : ""}</span>
              </>
            )}
            <Label>Created</Label><span>{date(inv.CreatedAt)}</span>
            <Label>External sync</Label><span>{renderSync(sync)}</span>
          </div>

          {fees.length > 0 && (
            <div>
              <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "var(--tracking-wider)", marginBottom: "var(--space-1)" }}>
                Amounts
              </div>
              <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
                  <thead>
                    <tr>
                      <th style={th}>Currency</th>
                      <th style={thR}>Payout</th>
                      <th style={thR}>Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map((f, i) => (
                      <tr key={i}>
                        <td style={td}>{f.currency ?? "—"}</td>
                        <td style={tdR}><strong>{money(f.revenueShareAmount)}</strong> {f.currency}</td>
                        <td style={tdR}>{money(f.totalFee)} {f.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <PayoutTargetPanel
            target={ptTarget}
            loading={ptLoading}
            payoutExpected={(inv.BillingMode ?? "Payout").toLowerCase() !== "feeinvoice"}
          />

          <StatementSheetsPanel page="statementInvoice" itemId={inv.ID} />
        </div>
      )}
    </GridEditModal>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "var(--color-text-muted)" }}>{children}</span>;
}

function renderSync(s: SyncInfo | null) {
  if (!s) return <Tag variant="warning">Not synced</Tag>;
  const moves = s.MoveIds?.length ? ` · moves ${s.MoveIds.join(",")}` : "";
  if (s.Status === "POSTED")
    return <span title={s.At ? `At ${s.At}` : undefined}><Tag variant="success" dot>Posted</Tag><span style={muted}>{moves}</span></span>;
  if (s.Status === "DRAFT")
    return <span title={s.At ? `At ${s.At}` : undefined}><Tag variant="info" dot>Draft</Tag><span style={muted}>{moves}</span></span>;
  if (s.Status === "FAILED")
    return <span title={s.Error ?? undefined}><Tag variant="danger" dot>Failed</Tag></span>;
  return <Tag>{s.Status ?? "—"}</Tag>;
}

function parseApprover(json: string | null | undefined): { By?: number; At?: string } | null {
  if (!json) return null;
  try { return JSON.parse(json) as { By?: number; At?: string }; } catch { return null; }
}

function parseFees(json: string | null | undefined): FeeEntry[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? (v as FeeEntry[]) : [];
  } catch { return []; }
}

function parseSync(json: string | null | undefined): SyncInfo | null {
  if (!json) return null;
  try { return JSON.parse(json) as SyncInfo; } catch { return null; }
}

const mono: React.CSSProperties = { fontFamily: "var(--font-family-mono, monospace)" };
const muted: React.CSSProperties = { color: "var(--color-text-muted)" };
const th: React.CSSProperties = { textAlign: "left", padding: "var(--space-1) var(--space-2)", borderBottom: "1px solid var(--color-border-default)", color: "var(--color-text-muted)", fontWeight: 600 };
const thR: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "var(--space-1) var(--space-2)", borderBottom: "1px solid var(--color-bg-muted)" };
const tdR: React.CSSProperties = { ...td, textAlign: "right" };

function date(s: string | null | undefined): string {
  if (!s) return "—";
  return s.length >= 10 ? s.slice(0, 10) : s;
}
function money(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
